import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Gift, Copy, Share2, Trophy, Users, Coins, Store, Sparkles, MessageCircle, Linkedin, Twitter, Mail } from 'lucide-react';
import PointsExplainer from '@/components/marketplace/PointsExplainer';
import WaysToEarnPoints from '@/components/marketplace/WaysToEarnPoints';

const TIER_COLORS = {
  none: 'bg-muted text-muted-foreground',
  bronze: 'bg-orange-500/15 text-orange-700 dark:text-orange-400',
  silver: 'bg-slate-400/20 text-slate-600 dark:text-slate-300',
  gold: 'bg-amber-500/15 text-amber-700 dark:text-amber-500',
  platinum: 'bg-purple-500/15 text-purple-700 dark:text-purple-400',
};

const InviteAndEarn = () => {
  const [data, setData] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [history, setHistory] = useState([]);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [me, rw, refs, hist, prog] = await Promise.all([
      api.get('/referrals/me').then(r => r.data),
      api.get('/referrals/rewards').then(r => r.data),
      api.get('/referrals/my-referrals').then(r => r.data),
      api.get('/referrals/points/history').then(r => r.data),
      api.get('/points/my-progress').then(r => r.data).catch(() => null),
    ]);
    setData(me); setRewards(rw); setReferrals(refs); setHistory(hist.history); setProgress(prog);
  };

  useEffect(() => { load().catch(() => toast.error('Failed to load')); }, []);

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };

  const share = (network) => {
    if (!data) return;
    const msg = `Join me on IndusFact — India's B2B industrial marketplace. Use my code ${data.referral_code} to get 100 points on signup: ${data.referral_link}`;
    const enc = encodeURIComponent;
    let url = '';
    switch (network) {
      case 'whatsapp': url = `https://api.whatsapp.com/send?text=${enc(msg)}`; break;
      case 'linkedin': url = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(data.referral_link)}`; break;
      case 'twitter': url = `https://twitter.com/intent/tweet?url=${enc(data.referral_link)}&text=${enc('Join me on IndusFact — B2B industrial marketplace')}`; break;
      case 'email': window.location.href = `mailto:?subject=Join%20me%20on%20IndusFact&body=${enc(msg)}`; return;
      default: return;
    }
    window.open(url, '_blank', 'noopener,noreferrer,width=650,height=560');
  };

  const redeem = async (r) => {
    if (!confirm(`Redeem "${r.name}" for ${r.cost_points} points?`)) return;
    setBusy(true);
    try {
      const { data: res } = await api.post('/referrals/rewards/redeem', { reward_id: r.id });
      toast.success(res.fulfilment?.note || 'Reward redeemed');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className='py-10 text-sm text-muted-foreground'>Loading…</div>;

  const rupeeValue = data.points_per_inr > 0 ? Math.round(data.balance / data.points_per_inr) : 0;

  return (
    <div className='space-y-4' data-testid='invite-and-earn-page'>
      <div>
        <h1 className='text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2'>
          <Gift className='h-6 w-6 text-primary' /> Invite & Earn
        </h1>
        <p className='text-muted-foreground text-sm'>Share IndusFact with peers and earn points every time someone signs up, posts an ad, or upgrades. Points redeem for ad packs, subscriptions, boosts, and cash-back coupons.</p>
      </div>

      {/* Wave 1.5 — How Points Work (transparent explainer + Cash vs Points + T&C) */}
      <PointsExplainer
        pointsPerInr={data.points_per_inr || 5}
        cashbackPct={progress?.purchase_cashback_pct || 4}
        globalCapFree={progress?.global_monthly_cap || 300}
        globalCapPaid={600}
        inactivityMonths={progress?.inactivity_expiry_months || 12}
        examples={rewards
          .filter(r => r.derived_pricing || (r.kind === 'issue_coupon' && r.meta?.retail_inr))
          .slice(0, 5)
          .map(r => ({
            name: r.name,
            retail: r.derived_pricing ? r.grand_total_inr : r.meta.retail_inr,
            pts: r.cost_points,
          }))}
      />

      {/* Hero card — code + tier + balance */}
      <Card className='p-5 border-primary/30 bg-primary/5' data-testid='referral-hero-card'>
        <div className='grid md:grid-cols-3 gap-4 items-center'>
          <div>
            <div className='text-xs uppercase text-muted-foreground mb-1'>Your referral code</div>
            <div className='flex items-center gap-2'>
              <code className='text-2xl font-bold text-primary tracking-wider' data-testid='referral-code'>{data.referral_code}</code>
              <Button size='sm' variant='outline' onClick={() => copy(data.referral_code)} data-testid='copy-code-btn'>
                <Copy className='h-3.5 w-3.5' />
              </Button>
            </div>
            <div className='flex items-center gap-1.5 text-xs mt-2'>
              <Input value={data.referral_link} readOnly className='h-8 text-xs' data-testid='referral-link' />
              <Button size='sm' variant='outline' onClick={() => copy(data.referral_link)}><Copy className='h-3.5 w-3.5' /></Button>
            </div>
          </div>
          <div className='text-center'>
            <div className='text-xs uppercase text-muted-foreground'>Points balance</div>
            <div className='text-4xl font-semibold tabular-nums mt-1' data-testid='points-balance'>{data.balance}</div>
            <div className='text-xs text-muted-foreground'>≈ ₹ {rupeeValue.toLocaleString('en-IN')} value</div>
          </div>
          <div className='text-center'>
            <div className='text-xs uppercase text-muted-foreground'>Referrer tier</div>
            <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold mt-1 capitalize ${TIER_COLORS[data.tier] || TIER_COLORS.none}`} data-testid='referral-tier'>
              <Trophy className='h-4 w-4' /> {data.tier}
            </div>
            {data.next_tier && (
              <div className='text-[11px] text-muted-foreground mt-1'>
                {data.next_tier.needed} more paying referral(s) to reach <strong className='capitalize'>{data.next_tier.name}</strong>
              </div>
            )}
          </div>
        </div>

        {/* Share buttons */}
        <div className='mt-4 pt-4 border-t border-primary/20'>
          <div className='text-xs uppercase text-muted-foreground mb-2'>Share your link</div>
          <div className='flex flex-wrap gap-2'>
            <Button size='sm' variant='outline' onClick={() => share('whatsapp')} data-testid='ref-share-whatsapp'>
              <MessageCircle className='h-4 w-4 mr-1.5 text-[#25D366]' /> WhatsApp
            </Button>
            <Button size='sm' variant='outline' onClick={() => share('linkedin')} data-testid='ref-share-linkedin'>
              <Linkedin className='h-4 w-4 mr-1.5 text-[#0A66C2]' /> LinkedIn
            </Button>
            <Button size='sm' variant='outline' onClick={() => share('twitter')} data-testid='ref-share-twitter'>
              <Twitter className='h-4 w-4 mr-1.5' /> X (Twitter)
            </Button>
            <Button size='sm' variant='outline' onClick={() => share('email')} data-testid='ref-share-email'>
              <Mail className='h-4 w-4 mr-1.5' /> Email
            </Button>
          </div>
        </div>
      </Card>

      {/* Stat tiles */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <Card className='p-4'>
          <div className='text-xs uppercase text-muted-foreground'>Signups</div>
          <div className='text-2xl font-semibold mt-1' data-testid='stat-signups'>{data.signups}</div>
        </Card>
        <Card className='p-4'>
          <div className='text-xs uppercase text-muted-foreground'>Posted first ad</div>
          <div className='text-2xl font-semibold mt-1'>{data.first_ad_posted}</div>
        </Card>
        <Card className='p-4'>
          <div className='text-xs uppercase text-muted-foreground'>Paying</div>
          <div className='text-2xl font-semibold mt-1' data-testid='stat-paying'>{data.paying_referrals}</div>
        </Card>
        <Card className='p-4'>
          <div className='text-xs uppercase text-muted-foreground'>Conversion</div>
          <div className='text-2xl font-semibold mt-1'>{data.signups > 0 ? Math.round((data.paying_referrals / data.signups) * 100) : 0}%</div>
        </Card>
      </div>

      <Tabs defaultValue='rewards' className='mt-4'>
        <TabsList>
          <TabsTrigger value='rewards' data-testid='tab-rewards'><Store className='h-4 w-4 mr-1.5' /> Rewards store</TabsTrigger>
          <TabsTrigger value='earn' data-testid='tab-earn'><Sparkles className='h-4 w-4 mr-1.5' /> Ways to earn</TabsTrigger>
          <TabsTrigger value='referrals' data-testid='tab-referrals'><Users className='h-4 w-4 mr-1.5' /> My referrals</TabsTrigger>
          <TabsTrigger value='history' data-testid='tab-history'><Coins className='h-4 w-4 mr-1.5' /> Points history</TabsTrigger>
        </TabsList>

        <TabsContent value='rewards' className='space-y-3 mt-4'>
          <div className='text-sm text-muted-foreground'>Spend your points on any of these rewards. {data.points_per_inr} points = ₹1 value.</div>
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-3'>
            {rewards.map(r => (
              <Card key={r.id} className='p-4 flex flex-col' data-testid={`reward-card-${r.id}`}>
                <div className='flex items-start justify-between gap-2 mb-2'>
                  <div className='font-semibold'>{r.name}</div>
                  <Badge variant='outline' className='text-[10px] capitalize'>{r.category}</Badge>
                </div>
                <div className='text-2xl font-semibold tabular-nums mt-1'>{r.cost_points} <span className='text-sm text-muted-foreground font-normal'>pts</span></div>
                <div className='text-xs text-muted-foreground'>≈ ₹{Math.round(r.cost_points / data.points_per_inr).toLocaleString('en-IN')}</div>
                <Button
                  size='sm'
                  className='mt-3'
                  disabled={busy || data.balance < r.cost_points}
                  onClick={() => redeem(r)}
                  data-testid={`redeem-${r.id}`}
                >
                  {data.balance >= r.cost_points ? 'Redeem' : `${r.cost_points - data.balance} more pts needed`}
                </Button>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value='earn' className='space-y-3 mt-4'>
          <WaysToEarnPoints progress={progress} />
        </TabsContent>

        <TabsContent value='referrals' className='space-y-3 mt-4'>
          {referrals.length === 0 ? (
            <div className='py-10 text-center text-muted-foreground text-sm'>No referrals yet. Share your link to get started.</div>
          ) : (
            <Card className='p-0 overflow-hidden'>
              <table className='w-full text-sm' data-testid='my-referrals-table'>
                <thead className='text-left border-b'>
                  <tr>
                    <th className='py-2 px-4'>Referee</th>
                    <th className='py-2 px-4'>Signed up</th>
                    <th className='py-2 px-4'>First ad</th>
                    <th className='py-2 px-4'>Paid</th>
                    <th className='py-2 px-4'>Points earned</th>
                    <th className='py-2 px-4'>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map(r => (
                    <tr key={r.id} className='border-b last:border-b-0'>
                      <td className='py-2 px-4'>
                        <div className='font-medium'>{r.referee_name || '—'}</div>
                        <div className='text-xs text-muted-foreground'>{r.referee_email_masked}</div>
                      </td>
                      <td className='py-2 px-4 text-xs text-muted-foreground'>{r.stage1_ts ? new Date(r.stage1_ts).toLocaleDateString('en-IN') : '—'}</td>
                      <td className='py-2 px-4 text-xs text-muted-foreground'>{r.stage2_ts ? new Date(r.stage2_ts).toLocaleDateString('en-IN') : '—'}</td>
                      <td className='py-2 px-4 text-xs text-muted-foreground'>{r.stage3_ts ? new Date(r.stage3_ts).toLocaleDateString('en-IN') : '—'}</td>
                      <td className='py-2 px-4 font-medium text-primary'>{r.referrer_points_awarded}</td>
                      <td className='py-2 px-4'>
                        <Badge variant='outline' className={`text-[10px] capitalize ${r.status === 'verified' ? 'text-[hsl(var(--verified))]' : r.status === 'pending' ? 'text-yellow-600' : ''}`}>
                          {r.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value='history' className='space-y-2 mt-4'>
          {history.length === 0 ? (
            <div className='py-10 text-center text-muted-foreground text-sm'>No points activity yet.</div>
          ) : (
            <Card className='p-0'>
              <table className='w-full text-sm'>
                <thead className='text-left border-b'>
                  <tr>
                    <th className='py-2 px-4'>When</th>
                    <th className='py-2 px-4'>Source</th>
                    <th className='py-2 px-4 text-right'>Change</th>
                    <th className='py-2 px-4 text-right'>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id} className='border-b last:border-b-0'>
                      <td className='py-2 px-4 text-xs text-muted-foreground'>{new Date(h.ts).toLocaleString('en-IN')}</td>
                      <td className='py-2 px-4'><code className='text-xs'>{h.source}</code></td>
                      <td className={`py-2 px-4 text-right font-medium tabular-nums ${h.delta > 0 ? 'text-[hsl(var(--verified))]' : 'text-[hsl(var(--danger))]'}`}>
                        {h.delta > 0 ? '+' : ''}{h.delta}
                      </td>
                      <td className='py-2 px-4 text-right font-medium tabular-nums'>{h.balance_after}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InviteAndEarn;
